/* Keep browsing music alive and update regional purchase options without leaving the page. */
(() => {
  'use strict';
  const player = document.getElementById('AvalonSound');
  if (!player || window.Shopify?.designMode || !window.fetch || !window.AbortController) return;
  const status = document.getElementById('AvalonNavigationStatus');
  const paths = new Set(['/pages/faq', '/cart']);
  ['.avalon-header__brand', '[data-avalon-membership-link]'].forEach(selector => {
    const link = document.querySelector(selector);
    if (link) paths.add(new URL(link.href, location.href).pathname);
  });
  const stateKey = 'avalonNavigation';
  let request = null;
  let revision = 0;
  let managed = false;
  let scrollTimer;
  const regionLinks = '.avalon-membership-regions__links a[href]';
  const purchaseCopy = '.avalon-reference-join__copy';
  const regionScripts = new Set([...document.querySelectorAll('script[src]')].map(script => script.src));
  const choiceValue = () => document.querySelector('avalon-purchase-options input:checked')?.value;

  function regional(url) {
    return url.origin === location.origin &&
      [...url.searchParams.keys()].every(key => key === 'preview_theme_id') &&
      [...document.querySelectorAll(regionLinks)].some(link => new URL(link.href).pathname === url.pathname);
  }
  function regionBusy(busy) {
    document.querySelectorAll('avalon-purchase-options, .avalon-delivery-country').forEach(node => node.toggleAttribute('inert', busy));
  }

  function eligible(url) {
    return url.origin === location.origin && paths.has(url.pathname) &&
      [...url.searchParams.keys()].every(key => key === 'preview_theme_id') &&
      url.searchParams.get('preview_theme_id') === new URL(location.href).searchParams.get('preview_theme_id');
  }
  function enabled() {
    if (player.getAttribute('aria-pressed') === 'true') return true;
    try { return localStorage.getItem('avalon-music-preference') === 'on'; } catch (_) { return false; }
  }
  function snapshot() {
    if (!managed) return;
    history.replaceState({...history.state, [stateKey]: {url:location.href,x:scrollX,y:scrollY,choice:choiceValue()}}, '', location.href);
  }
  function fallback(url) {
    // Observable for diagnostics; ordinary production behavior remains a native navigation.
    const event = new CustomEvent('avalon:navigation-fallback', {cancelable:true, detail:{url:String(url)}});
    if (document.dispatchEvent(event)) location.assign(String(url));
  }
  function unsafe(main) {
    return main.querySelector('script:not([type="application/ld+json"]):not([type="application/json"]), .shopify-app-block, iframe');
  }
  function metadata(next) {
    document.title = next.title;
    for (const selector of ['link[rel="canonical"]','meta[name="description"]','meta[property^="og:"]','meta[name^="twitter:"]']) {
      document.head.querySelectorAll(selector).forEach(node => node.remove());
      next.head.querySelectorAll(selector).forEach(node => document.head.append(node.cloneNode(true)));
    }
  }
  function regionContent(next, url) {
    const copy = next.querySelector(purchaseCopy);
    const active = copy?.querySelector(`${regionLinks}[aria-current="page"]`);
    if (!copy || !active || new URL(active.getAttribute('href'), url).pathname !== url.pathname ||
        !copy.querySelector('avalon-purchase-options') || !customElements.get('avalon-purchase-options') ||
        copy.querySelector('.shopify-app-block, iframe')) throw new Error('Native region required');
    // Only the already-registered Avalon component is safe to reuse without running scripts.
    for (const script of copy.querySelectorAll('script')) {
      if (['application/ld+json', 'application/json'].includes(script.type)) continue;
      const src = script.getAttribute('src');
      const absolute = src && new URL(src, url);
      if (!absolute || !absolute.pathname.endsWith('/avalon-purchase-options.js') || !regionScripts.has(absolute.href)) throw new Error('New region dependency');
      script.remove();
    }
    const styles = new Set([...document.querySelectorAll('link[rel="stylesheet"]')].map(link => link.href));
    if ([...copy.querySelectorAll('link[rel="stylesheet"]')].some(link => !styles.has(new URL(link.getAttribute('href'), url).href))) throw new Error('New region stylesheet');
    return copy;
  }
  async function navigate(url, restore = null, region = false) {
    const attempt = ++revision;
    request?.abort();
    const controller = new AbortController();
    request = controller;
    const timeout = setTimeout(() => controller.abort(), 8000);
    const current = document.getElementById('MainContent');
    current?.setAttribute('aria-busy', 'true');
    regionBusy(region);
    if (status) status.textContent = 'Loading page…';
    document.dispatchEvent(new CustomEvent('avalon:before-navigate'));
    try {
      const response = await fetch(url.href, {credentials:'same-origin', cache:'no-store', signal:controller.signal});
      if (!response.ok || response.redirected || !response.headers.get('content-type')?.includes('text/html')) throw new Error('Native response required');
      const next = new DOMParser().parseFromString(await response.text(), 'text/html');
      if (attempt !== revision) return;
      const main = next.getElementById('MainContent');
      if (region) {
        const oldCopy = current?.querySelector(purchaseCopy);
        const copy = regionContent(next, url);
        if (!oldCopy) throw new Error('Missing purchase area');
        // Measure immediately before the swap so scrolling during a slow request is respected.
        const top = oldCopy.querySelector('.avalon-membership-regions').getBoundingClientRect().top;
        const wanted = restore?.choice || choiceValue() || 'membership';
        const choices = [...copy.querySelectorAll('avalon-purchase-options input[type="radio"]')];
        const selected = choices.find(input => input.value === wanted) || choices.find(input => input.value === 'membership');
        if (!selected) throw new Error('Missing purchase choice');
        choices.forEach(input => { input.checked = input === selected; });
        if (!managed) { managed = true; history.scrollRestoration = 'manual'; }
        if (!restore) {
          snapshot();
          history.pushState({...history.state,[stateKey]:{url:url.href,x:scrollX,y:scrollY,choice:selected.value}}, '', url.href);
        }
        oldCopy.replaceWith(copy);
        // connectedCallback also handles one-time deep links; restore the explicit history choice.
        choices.forEach(input => { input.checked = input === selected; });
        copy.querySelector('avalon-purchase-options').update();
        metadata(next);
        copy.querySelector(`${regionLinks}[aria-current="page"]`).focus({preventScroll:true});
        const newTop = copy.querySelector('.avalon-membership-regions').getBoundingClientRect().top;
        window.scrollTo({left:restore?.x ?? scrollX, top:restore?.y ?? scrollY + newTop - top, behavior:'instant'});
        snapshot();
        if (status) status.textContent = `${document.title}. ${selected.value !== wanted ? 'That purchase option is unavailable here; monthly membership selected.' : 'Purchase options updated.'}`;
        return;
      }
      if (!current || !main || !next.body.classList.contains('avalon') || !next.getElementById('AvalonSound') || unsafe(main) || unsafe(current)) throw new Error('Native initialization required');
      // All four pages share the layout's assets; unknown page-specific dependencies need a real load.
      const loadedScripts = new Set([...document.querySelectorAll('script[src]')].map(s=>s.getAttribute('src')));
      if ([...next.querySelectorAll('script[src]')].some(s=>!loadedScripts.has(s.getAttribute('src')))) throw new Error('New script dependency');
      if (!managed) {
        managed = true;
        history.scrollRestoration = 'manual';
      }
      if (!restore) {
        snapshot();
        history.pushState({...history.state,[stateKey]:{url:url.href,x:0,y:0}}, '', url.href);
      }
      current.replaceWith(main);
      metadata(next);
      document.querySelectorAll('.avalon-header a, [data-avalon-drawer] a').forEach(link => {
        const active = new URL(link.href, location.href).pathname === url.pathname;
        if (active) link.setAttribute('aria-current','page'); else link.removeAttribute('aria-current');
      });
      // Reinitialize only Avalon's owned page behavior, not analytics/app/global scripts.
      document.dispatchEvent(new CustomEvent('avalon:navigated'));
      if (window.StandardEvents?.PageViewEvent) {
        document.dispatchEvent(new window.StandardEvents.PageViewEvent({page:{template:main.dataset.template || '',title:document.title,url:location.href}}));
      }
      main.focus({preventScroll:true});
      if (restore) window.scrollTo(restore.x || 0, restore.y || 0);
      else if (url.hash) {
        let id;
        try { id = decodeURIComponent(url.hash.slice(1)); } catch (_) { id = ''; }
        const target = document.getElementById(id);
        if (target) target.scrollIntoView(); else window.scrollTo(0,0);
      } else window.scrollTo(0,0);
      if (status) status.textContent = document.title;
    } catch (_) {
      if (attempt === revision) fallback(url);
    } finally {
      clearTimeout(timeout);
      if (attempt === revision) {
        request = null;
        regionBusy(false);
        document.getElementById('MainContent')?.removeAttribute('aria-busy');
        if (status && status.textContent === 'Loading page…') status.textContent = '';
      }
    }
  }
  document.addEventListener('click', event => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest('a[href]');
    if (!link || link.hasAttribute('download') || (link.target && link.target !== '_self') || link.closest('[data-avalon-native-navigation]')) return;
    const url = new URL(link.href, location.href);
    const region = !!link.matches(regionLinks) && regional(url) && regional(new URL(location.href));
    if (region) {
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview && !url.searchParams.has('preview_theme_id')) url.searchParams.set('preview_theme_id', preview);
      if (url.searchParams.get('preview_theme_id') !== new URL(location.href).searchParams.get('preview_theme_id')) return;
    } else if (!enabled() || !eligible(url) || !eligible(new URL(location.href))) return;
    if (url.pathname === location.pathname && url.search === location.search && url.hash) return;
    event.preventDefault();
    // An earlier consented listener may need this real click to unlock a new-tab context.
    if (!region && player.getAttribute('aria-pressed') !== 'true' && !player.disabled) player.click();
    if (url.href === location.href || (region && link.hasAttribute('aria-current'))) {
      revision++;
      request?.abort();
      request = null;
      regionBusy(false);
      document.getElementById('MainContent')?.removeAttribute('aria-busy');
      if (status) status.textContent = '';
      document.dispatchEvent(new CustomEvent('avalon:before-navigate'));
      if (!region) window.scrollTo(0,0);
      return;
    }
    navigate(url, null, region);
  });
  window.addEventListener('popstate', event => {
    if (!managed) return;
    const url = new URL(location.href);
    const region = regional(url);
    if (!event.state?.[stateKey] || (!region && !eligible(url))) return fallback(url);
    navigate(url, event.state[stateKey], region);
  });
  document.addEventListener('submit', event => {
    if (request && event.target.closest(purchaseCopy)) event.preventDefault();
  }, true);
  window.addEventListener('scroll', () => {
    clearTimeout(scrollTimer);
    scrollTimer = setTimeout(() => { if (!request) snapshot(); }, 150);
  }, {passive:true});
})();
